/**
 * Real-Time Device Management (RTDM) service.
 *
 * Owns all data access for the RTDM tabs. Backed by the wbc-api-gateway
 * endpoints under /api/v1/rtdm/ (see RTDM_Postman_Collection.json).
 *
 * Public methods are Promise-shaped; components do not change when the backend
 * URL or request shape changes — only this file does.
 *
 * Error contract: methods throw on network/timeout errors (like deviceDataService)
 * so components can catch and display a message.
 */

// ── Reference data ──────────────────────────────────────────────────────────

export const METRIC_LABELS = {
  rsrp: 'RSRP',
  rsrq: 'RSRQ',
  sinr: 'SINR',
  serving: 'Serving Cell Metrics',
  neighbor: 'Neighbor Cell Metrics',
  throughput: 'Throughput Performance',
  'modem-ssr': 'Modem SSR',
  'device-recovery': 'Device Recovery',
};

export const MONITOR_METRIC_OPTIONS = [
  { value: 'rsrp', label: 'RSRP — Reference Signal Received Power' },
  { value: 'rsrq', label: 'RSRQ — Reference Signal Received Quality' },
  { value: 'sinr', label: 'SINR — Signal to Interference & Noise Ratio' },
  { value: 'serving', label: 'Serving Cell Metrics' },
  { value: 'neighbor', label: 'Neighbor Cell Metrics' },
  { value: 'throughput', label: 'Throughput Performance' },
];

export const RUN_ACTION_OPTIONS = [
  { value: 'rsrp', label: 'RSRP — Get Signal Strength (RSRP)' },
  { value: 'rsrq', label: 'RSRQ — Get Signal Strength (RSRQ)' },
  { value: 'sinr', label: 'SINR — Get Signal Strength (SINR)' },
  { value: 'serving', label: 'Get Serving Cell Info' },
  { value: 'neighbor', label: 'Get Neighbor Cell Info' },
  { value: 'throughput', label: 'Get Throughput (one-shot)' },
  { value: 'modem-ssr', label: 'Modem SSR' },
  { value: 'device-recovery', label: 'Device Recovery' },
];

export const INTERVAL_OPTIONS = [
  { value: '10s', label: '10s' },
  { value: '20s', label: '20s' },
  { value: '30s', label: '30s' },
  { value: '60s', label: '60s' },
  { value: '5 min', label: '5 min' },
  { value: '10 min', label: '10 min' },
];

export const THROUGHPUT_INTERVAL_OPTIONS = [
  { value: '5 min', label: '5 min' },
  { value: '10 min', label: '10 min' },
  { value: '15 min', label: '15 min' },
  { value: '30 min', label: '30 min' },
];

export const ACTION_ICONS = { monitor: '👁️', configure: '⚙️', run: '⚡' };

export const defaultThroughputConfig = () => ({
  testType: 'ul_dl',
  protocol: 'tcp',
  ipVersion: 'ipv4',
  durationSec: '10',
  serverAddress: '',
  portRangeStart: '5201',
  portRangeEnd: '5210',
  bandwidthKbps: '',
  parallelStreams: '1',
});

// ── Static mappings ──────────────────────────────────────────────────────────

// UI metric value → backend actionKey
const METRIC_TO_ACTION_KEY = {
  rsrp: 'signal-strength-rsrp',
  rsrq: 'signal-strength-rsrq',
  sinr: 'signal-strength-sinr',
  serving: 'serving-cell-metrics',
  neighbor: 'neighbor-cell-metrics',
  throughput: 'get-throughput',
  'modem-ssr': 'get-throughput', // backend has no dedicated key — best-effort
  'device-recovery': 'get-throughput', // backend has no dedicated key — best-effort
};

// Backend actionKey → UI metric value (for transform)
const ACTION_KEY_TO_METRIC = Object.fromEntries(
  Object.entries(METRIC_TO_ACTION_KEY).map(([k, v]) => [v, k])
);

// UI interval label → seconds
const INTERVAL_TO_SEC = {
  '10s': 10, '20s': 20, '30s': 30, '60s': 60,
  '5 min': 300, '10 min': 600, '15 min': 900, '30 min': 1800,
};

// seconds → UI interval label (closest match)
function secToIntervalLabel(sec) {
  const n = Number(sec);
  const entries = Object.entries(INTERVAL_TO_SEC);
  for (const [label, s] of entries) {
    if (s === n) return label;
  }
  return `${n}s`;
}

// UI testType → backend enum
const TEST_TYPE_MAP = { ul_dl: 'UPLINK_DOWNLINK', ul: 'UPLINK', dl: 'DOWNLINK' };
const TEST_TYPE_REVERSE = Object.fromEntries(Object.entries(TEST_TYPE_MAP).map(([k, v]) => [v, k]));

// SCS kHz → backend numeric index (3GPP subcarrier spacing numerology)
const SCS_KHZ_TO_IDX = { '15': 0, '30': 1, '60': 2, '120': 3, '240': 4 };
const SCS_IDX_TO_KHZ = Object.fromEntries(Object.entries(SCS_KHZ_TO_IDX).map(([k, v]) => [v, k]));

// ── Request builders ─────────────────────────────────────────────────────────

/**
 * Convert a single frontend action object to a backend action payload element.
 * Returns null for action types the backend has no key for.
 */
function buildActionPayload(action) {
  if (action.type === 'monitor') {
    const metric = action.metric || action.key?.replace('monitor:', '');
    const actionKey = METRIC_TO_ACTION_KEY[metric];
    if (!actionKey) return null;

    const periodicIntervalSec = INTERVAL_TO_SEC[action.interval] ?? 30;
    const payload = { actionKey, category: 'MONITOR', periodicIntervalSec };

    if (action.maxIterations) {
      const n = parseInt(action.maxIterations, 10);
      if (!isNaN(n) && n > 0) payload.maxSampleCount = n;
    }

    // Throughput monitor carries extra fields inline in the action body
    if (metric === 'throughput' && action.throughput) {
      Object.assign(payload, buildThroughputFields(action.throughput));
    }
    return payload;
  }

  if (action.type === 'configure') {
    if (action.key === 'configure:deprioritize5g') {
      return {
        actionKey: 'deprioritize-5g-control',
        category: 'CONFIGURE',
        deprioritize5G: action.deprioritize5G === true,
      };
    }

    const payload = { actionKey: 'cell-lock-list-configuration', category: 'CONFIGURE' };
    const rat = action.rat;

    if (rat === 'Clear') {
      const clearRat = action.clearRat || 'LTE';
      payload.cellLockRat = clearRat;
      if (clearRat === 'NR5G') {
        payload.cellLockType = 'UNLOCK';
      }
      // LTE clear = omit ltePci/lteEarfcn/lteCellList (empty = clear)
      return payload;
    }

    if (rat === 'LTE') {
      payload.cellLockRat = 'LTE';
      if (action.enforceNoPowerCycle) payload.lteEnforce = true;
      const cells = (action.lteCells || []).filter((c) => c.pci !== '' && c.earfcn !== '');
      if (cells.length === 1) {
        payload.ltePci = Number(cells[0].pci);
        payload.lteEarfcn = Number(cells[0].earfcn);
      } else if (cells.length > 1) {
        payload.lteCellList = cells.map((c) => ({ pci: Number(c.pci), earfcn: Number(c.earfcn) }));
      }
      return payload;
    }

    if (rat === 'NR5G') {
      payload.cellLockRat = 'NR5G';
      const lockType = action.nr5gLockType || 'PCI';
      payload.cellLockType = lockType;

      if (lockType === 'PCI') {
        const cells = (action.nr5gPciCells || []).filter((c) => c.pci !== '');
        if (cells.length > 0) {
          // Backend single-cell PCI path (multi-PCI not in contract; send first cell)
          const first = cells[0];
          payload.nr5gPci = Number(first.pci);
          if (first.arfcn !== '') payload.nr5gArfcn = Number(first.arfcn);
          if (first.scs !== '') payload.nr5gScs = SCS_KHZ_TO_IDX[String(first.scs)] ?? Number(first.scs);
          if (first.bandMask) payload.nr5gBandPrefMask = first.bandMask;
        }
      } else if (lockType === 'ARFCN') {
        // nr5gArfcns is a bare-ARFCN array in the UI — use default SCS (1 = 30 kHz)
        const arfcns = (action.nr5gArfcns || []).filter((a) => a !== '');
        if (arfcns.length > 0) {
          payload.nr5gArfcnList = arfcns.map((a) => ({ arfcn: Number(a), scs: 1 }));
        }
      }
      return payload;
    }

    return null;
  }

  if (action.type === 'run') {
    const metric = action.action || action.key?.replace('run:', '');
    if (metric === 'throughput' && action.throughput) {
      return {
        actionKey: 'get-throughput',
        category: 'RUN_ACTION',
        ...buildThroughputFields(action.throughput),
      };
    }
    // Backend has no dedicated key for one-shot signal/modem-ssr/device-recovery yet;
    // map to actionKey best-effort using the same METRIC_TO_ACTION_KEY table.
    const actionKey = METRIC_TO_ACTION_KEY[metric] || metric;
    return { actionKey, category: 'RUN_ACTION' };
  }

  return null;
}

function buildThroughputFields(tp) {
  const fields = {
    testServerAddr: tp.serverAddress || '',
    testServerPortStart: Number(tp.portRangeStart) || 5201,
    testServerPortEnd: Number(tp.portRangeEnd) || 5210,
    testType: TEST_TYPE_MAP[tp.testType] || 'UPLINK_DOWNLINK',
    durationSec: Number(tp.durationSec) || 10,
    transportProtocol: (tp.protocol || 'tcp').toUpperCase(),
    ipVersion: (tp.ipVersion || 'ipv4').toUpperCase(),
  };
  const bw = parseInt(tp.bandwidthKbps, 10);
  if (!isNaN(bw) && bw > 0) fields.bandwidthKbps = bw;
  return fields;
}

// ── Response transforms ──────────────────────────────────────────────────────

/**
 * Map a single backend action element → frontend action object for rendering.
 * Uses a stable id derived from actionKey so list keys remain stable.
 */
function transformAction(apiAction, index) {
  const key = apiAction.actionKey || '';
  const category = (apiAction.category || '').toUpperCase();

  if (category === 'MONITOR') {
    const metric = ACTION_KEY_TO_METRIC[key] || key;
    const interval = secToIntervalLabel(apiAction.periodicIntervalSec);
    const sub = `Every ${interval}${apiAction.maxSampleCount ? ` · max ${apiAction.maxSampleCount}` : ''}`;
    return {
      id: index + 1,
      type: 'monitor',
      key: `monitor:${metric}`,
      label: METRIC_LABELS[metric] || key,
      sub,
      icon: ACTION_ICONS.monitor,
      metric,
      interval,
      maxIterations: apiAction.maxSampleCount ? String(apiAction.maxSampleCount) : null,
    };
  }

  if (category === 'CONFIGURE') {
    const rat = apiAction.cellLockRat || 'LTE';
    const lockType = apiAction.cellLockType;
    let sub;
    let extraFields = {};

    if (lockType === 'UNLOCK' || (!apiAction.ltePci && !apiAction.lteCellList && rat === 'LTE')) {
      sub = `Clear cell lock · ${rat}`;
      extraFields = { rat: 'Clear', clearRat: rat };
    } else if (rat === 'LTE') {
      const count = apiAction.lteCellList?.length || (apiAction.ltePci !== undefined ? 1 : 0);
      sub = `LTE · ${count} cell${count !== 1 ? 's' : ''}`;
      extraFields = {
        rat: 'LTE',
        enforceNoPowerCycle: !!apiAction.lteEnforce,
        lteCells: apiAction.lteCellList
          ? apiAction.lteCellList.map((c, i) => ({ id: i, pci: String(c.pci), earfcn: String(c.earfcn) }))
          : [{ id: 0, pci: String(apiAction.ltePci ?? ''), earfcn: String(apiAction.lteEarfcn ?? '') }],
      };
    } else if (rat === 'NR5G') {
      const type = lockType === 'ARFCN' ? 'ARFCN' : 'PCI';
      const count = apiAction.nr5gArfcnList?.length || 1;
      sub = `NR5G · ${count} cell(s)`;
      extraFields = {
        rat: 'NR5G',
        nr5gLockType: type,
        nr5gPciCells: type === 'PCI'
          ? [{ id: 0, pci: String(apiAction.nr5gPci ?? ''), arfcn: String(apiAction.nr5gArfcn ?? ''), scs: String(SCS_IDX_TO_KHZ[apiAction.nr5gScs] ?? '30'), bandMask: apiAction.nr5gBandPrefMask || '' }]
          : [],
        nr5gArfcns: type === 'ARFCN'
          ? (apiAction.nr5gArfcnList || []).map((e) => String(e.arfcn))
          : [''],
      };
    } else {
      sub = 'Cell Lock';
      extraFields = { rat };
    }

    return {
      id: index + 1,
      type: 'configure',
      key: 'configure:celllock',
      label: 'Cell Lock',
      sub,
      icon: ACTION_ICONS.configure,
      ...extraFields,
    };
  }

  if (category === 'RUN_ACTION') {
    const metric = ACTION_KEY_TO_METRIC[key] || key;
    const label = METRIC_LABELS[metric] || key;
    return {
      id: index + 1,
      type: 'run',
      key: `run:${metric}`,
      label,
      sub: 'One-time action',
      icon: ACTION_ICONS.run,
      action: metric,
    };
  }

  // Unknown category — pass through minimally
  return {
    id: index + 1,
    type: 'run',
    key: key,
    label: key,
    sub: category,
    icon: ACTION_ICONS.run,
  };
}

// Backend signalType enum → actionKey
const SIGNAL_TYPE_TO_ACTION_KEY = {
  NETWORK_STATS_METRIC_TYPE_RSRP: 'signal-strength-rsrp',
  NETWORK_STATS_METRIC_TYPE_RSRQ: 'signal-strength-rsrq',
  NETWORK_STATS_METRIC_TYPE_SINR: 'signal-strength-sinr',
};

/**
 * Convert the backend configuredActions structure into the intermediate
 * {actionKey, category, ...} shape that transformAction() already handles.
 *
 * configuredActions: { getCfgs: [...], setCfgs: [...] }
 *
 * getCfgs entries (monitor):
 *   { monitorType, periodicIntervalSec, maxSampleCount,
 *     signalStrengthCfg: { signalType } }
 *   { monitorType, periodicIntervalSec, servingCellInfoCfg: {...} }
 *   { monitorType, periodicIntervalSec, neighborCellInfoCfg: {...} }
 *   { monitorType, periodicIntervalSec, throughputCfg: {...} }
 *
 * setCfgs entries:
 *   { cellLockListCfg: { lteCellLockCfg: { enforce, cellIdentityList:[{pci,freq}] } } }
 */
function transformConfiguredActions(configuredActions) {
  if (!configuredActions) return [];
  const results = [];

  for (const getCfg of (configuredActions.getCfgs || [])) {
    let actionKey = null;

    if (getCfg.signalStrengthCfg) {
      actionKey = SIGNAL_TYPE_TO_ACTION_KEY[getCfg.signalStrengthCfg.signalType] || 'signal-strength-rsrp';
    } else if (getCfg.servingCellInfoCfg) {
      actionKey = 'serving-cell-metrics';
    } else if (getCfg.neighborCellInfoCfg) {
      actionKey = 'neighbor-cell-metrics';
    } else if (getCfg.throughputCfg) {
      actionKey = 'get-throughput';
    } else if (getCfg.actionKey) {
      actionKey = getCfg.actionKey; // already mapped
    }

    if (!actionKey) continue;

    const apiAction = {
      actionKey,
      category: 'MONITOR',
      periodicIntervalSec: getCfg.periodicIntervalSec ?? 30,
    };
    if (getCfg.maxSampleCount) apiAction.maxSampleCount = getCfg.maxSampleCount;
    results.push(apiAction);
  }

  for (const setCfg of (configuredActions.setCfgs || [])) {
    if (setCfg.cellLockListCfg) {
      const cfg = setCfg.cellLockListCfg;
      const apiAction = { actionKey: 'cell-lock-list-configuration', category: 'CONFIGURE' };

      if (cfg.lteCellLockCfg) {
        apiAction.cellLockRat = 'LTE';
        apiAction.lteEnforce = cfg.lteCellLockCfg.enforce || false;
        const cells = (cfg.lteCellLockCfg.cellIdentityList || []);
        if (cells.length === 0) {
          // Empty list = clear/unlock LTE
          // Leave ltePci/lteEarfcn/lteCellList absent → transformAction treats it as Clear
        } else if (cells.length === 1) {
          apiAction.ltePci = cells[0].pci;
          apiAction.lteEarfcn = cells[0].freq; // backend uses "freq" for earfcn
        } else {
          apiAction.lteCellList = cells.map((c) => ({ pci: c.pci, earfcn: c.freq }));
        }
      } else if (cfg.nr5gCellLockCfg) {
        apiAction.cellLockRat = 'NR5G';
        const nr = cfg.nr5gCellLockCfg;
        if (nr.arfcnList && nr.arfcnList.length > 0) {
          apiAction.cellLockType = 'ARFCN';
          apiAction.nr5gArfcnList = nr.arfcnList.map((e) => ({ arfcn: e.arfcn, scs: e.scs ?? 1 }));
        } else {
          apiAction.cellLockType = 'PCI';
          if (nr.pci !== undefined) apiAction.nr5gPci = nr.pci;
          if (nr.arfcn !== undefined) apiAction.nr5gArfcn = nr.arfcn;
          if (nr.scs !== undefined) apiAction.nr5gScs = nr.scs;
          if (nr.bandMask) apiAction.nr5gBandPrefMask = nr.bandMask;
        }
      }
      results.push(apiAction);
    }
  }

  return results;
}

/**
 * Map a backend session object → the shape RTDM components expect.
 */
function transformSession(apiSession) {
  if (!apiSession) return null;

  // Backend uses sessionState; fall back to status for older/other shapes
  const statusRaw = (apiSession.sessionState || apiSession.status || '').toUpperCase();
  const status =
    statusRaw === 'ACTIVE' ? 'running' :
    statusRaw === 'INACTIVE' ? 'stopped' :
    statusRaw === 'SCHEDULED' ? 'scheduled' :
    apiSession.sessionState || apiSession.status || 'unknown';

  // Backend returns actions under configuredActions{getCfgs,setCfgs}; fall back to flat actions[]
  const rawActions = apiSession.configuredActions
    ? transformConfiguredActions(apiSession.configuredActions)
    : (apiSession.actions || []);
  const actions = rawActions.map(transformAction);

  return {
    sessionId: apiSession.sessionId || apiSession.session_id || '',
    systemId: apiSession.systemId || apiSession.clientId || '',
    status,
    startTime: apiSession.sessionStartTime ?? apiSession.startTime ?? apiSession.start_time ?? null,
    endTime: apiSession.sessionEndedAt ?? apiSession.endTime ?? apiSession.end_time ?? null,
    durationMins: apiSession.sessionTimeInMins ?? apiSession.durationMins ?? null,
    scheduledTime: apiSession.scheduledTime ?? null,
    createdUser: apiSession.userName || apiSession.user || apiSession.createdUser || '',
    version: apiSession.version || 'v1',
    configVersion: apiSession.configVersion ?? null,
    actionCount: actions.length,
    actions,
    configSummary: summarizeActions(actions),
    actionTypeCounts: countActionTypes(actions),
    rdSessionId: apiSession.rdSessionId || null,
    termination: apiSession.terminationType || apiSession.termination || (status === 'stopped' ? 'stopped' : undefined),
  };
}

/**
 * Map a backend scheduled-session object → the shape RTScheduledSessions expects.
 */
function transformScheduledSession(apiSession) {
  if (!apiSession) return null;
  const actions = (apiSession.actions || []).map(transformAction);
  const id = apiSession.scheduledSessionId || apiSession.sessionId || '';
  return {
    sessionId: id,
    scheduledSessionId: id,
    systemId: apiSession.systemId || '',
    status: 'scheduled',
    scheduledTime: apiSession.scheduledTime
      ? new Date(apiSession.scheduledTime).getTime()
      : null,
    durationMins: apiSession.sessionTimeInMins ?? apiSession.durationMins ?? null,
    createdUser: apiSession.scheduledBy || apiSession.user || '',
    schedulingMode: apiSession.schedulingMode || 'FIXED',
    version: apiSession.version || 'v1',
    actionCount: actions.length,
    actions,
  };
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function bumpVersion(version) {
  const n = parseInt(String(version || 'v1').replace(/^v/, ''), 10) || 1;
  return `v${n + 1}`;
}

function countActionTypes(actions = []) {
  return actions.reduce(
    (acc, a) => {
      if (a.type === 'monitor') acc.monitor += 1;
      else if (a.type === 'configure') acc.configure += 1;
      else if (a.type === 'run') acc.run += 1;
      return acc;
    },
    { monitor: 0, configure: 0, run: 0 }
  );
}

function summarizeActions(actions = []) {
  const counts = countActionTypes(actions);
  const parts = [];
  if (counts.monitor) parts.push(`👁 ${counts.monitor}`);
  if (counts.configure) parts.push(`⚙ ${counts.configure}`);
  if (counts.run) parts.push(`⚡ ${counts.run}`);
  return parts.join(', ') || '—';
}

// Default collectorId used across all RTDM sessions.
const DEFAULT_COLLECTOR_ID = 737629725;

// ── Service ──────────────────────────────────────────────────────────────────

class RealTimeDeviceService {
  constructor() {
    const wbcApiHost = (process.env.REACT_APP_WBC_API_BASE_URL || 'http://localhost:8080').replace(/\/$/, '');
    this.baseUrl = `${wbcApiHost}/api/v1/rtdm`;
    this.timeout = parseInt(process.env.REACT_APP_PANEL_API_TIMEOUT, 10) || 60000;

    // Set by RealTimeDeviceManagement on mount via setUserContext()
    this._userEmail = '';
    this._userRole = 'Admin';
  }

  /** Called by RealTimeDeviceManagement when userInfo / role are available. */
  setUserContext(email, role) {
    this._userEmail = email || this._userEmail || '';
    this._userRole = role || 'Admin';
  }

  _resolveUser() {
    return this._userEmail || sessionStorage.getItem('cognitoIdEmail') || 'unknown@qualcomm.com';
  }

  // ── Private fetch helper ──────────────────────────────────────────────────

  async _fetch(path, options = {}) {
    const url = `${this.baseUrl}${path}`;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    const cognitoToken = sessionStorage.getItem('cognitoIdToken');
    const headers = { 'Content-Type': 'application/json', ...options.headers };
    if (cognitoToken) headers['X-User-Token'] = cognitoToken;
    if (this._userEmail) headers['X-User-Email'] = this._userEmail;

    try {
      const response = await fetch(url, { ...options, headers, signal: controller.signal });
      clearTimeout(timeoutId);

      if (!response.ok) {
        let errMsg = `HTTP ${response.status}`;
        try { const body = await response.json(); errMsg = body.message || body.error || errMsg; } catch (_) {}
        throw new Error(errMsg);
      }

      // 204 No Content
      if (response.status === 204) return null;
      return await response.json();
    } catch (err) {
      clearTimeout(timeoutId);
      if (err.name === 'AbortError') {
        throw new Error(`RTDM request timed out after ${this.timeout / 1000}s`);
      }
      throw err;
    }
  }

  // ── Public API ────────────────────────────────────────────────────────────

  /** List active sessions across all devices. */
  async getActiveSessions(systemId = '') {
    const path = systemId
      ? `/sessions/${encodeURIComponent(systemId)}?status=ACTIVE&page=0&size=100`
      : `/sessions?status=ACTIVE&page=0&size=100`;
    const data = await this._fetch(path);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || data?.data || []);
    return list.map(transformSession);
  }

  /** List completed/inactive sessions across all devices. */
  async getCompletedSessions(systemId = '') {
    const path = systemId
      ? `/sessions/${encodeURIComponent(systemId)}?status=INACTIVE&page=0&size=100`
      : `/sessions?status=INACTIVE&page=0&size=100`;
    const data = await this._fetch(path);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || data?.data || []);
    return list.map(transformSession);
  }

  /** List scheduled sessions. Admin sees all; others see their own. */
  async getScheduledSessions() {
    const params = new URLSearchParams({
      user: this._resolveUser(),
      userRole: this._userRole,
      status: 'SCHEDULED',
      page: '0',
      size: '100',
    });
    const data = await this._fetch(`/scheduled-sessions?${params}`);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || []);
    return list.map(transformScheduledSession);
  }

  /** Paged active sessions for the View tab. page is 0-indexed. Returns { sessions, total }. */
  async getActiveSessionsPaged(page = 0, size = 10) {
    const path = `/sessions?status=ACTIVE&page=${page}&size=${size}`;
    const data = await this._fetch(path);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || data?.data || []);
    return { sessions: list.map(transformSession), total: data?.total ?? list.length };
  }

  /** Paged completed sessions for the View tab. page is 0-indexed. Returns { sessions, total }. */
  async getCompletedSessionsPaged(page = 0, size = 10) {
    const path = `/sessions?status=INACTIVE&page=${page}&size=${size}`;
    const data = await this._fetch(path);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || data?.data || []);
    return { sessions: list.map(transformSession), total: data?.total ?? list.length };
  }

  /** Paged scheduled sessions for the View tab. page is 1-indexed. Returns { sessions, total }. */
  async getScheduledSessionsPaged(page = 1, size = 10) {
    const params = new URLSearchParams({
      user: this._resolveUser(),
      userRole: this._userRole,
      status: 'SCHEDULED',
      page: String(page),
      size: String(size),
    });
    const data = await this._fetch(`/scheduled-sessions?${params}`);
    const list = Array.isArray(data) ? data : (data?.sessions || data?.content || []);
    const total = data?.pagination?.totalElements ?? data?.total ?? list.length;
    return { sessions: list.map(transformScheduledSession), total };
  }

  /** Fetch the full status/detail of a single session. */
  async getSession(sessionId, systemId = '') {
    if (!sessionId) return null;
    // Try to derive systemId from the cached session list if not provided
    const sid = systemId || this._systemIdCache?.[sessionId] || '';
    if (!sid) {
      // Without systemId we can't call the gateway endpoint — return null
      // (caller falls back to the already-loaded row object)
      return null;
    }
    const data = await this._fetch(`/sessions/${encodeURIComponent(sid)}/${encodeURIComponent(sessionId)}/status`);
    return transformSession(data);
  }

  /**
   * Fetch the latest live telemetry snapshot for a session (from Redis).
   * Returns the raw gateway shape:
   *   { sessionId, systemId, fetchedAt, data: { [actionKey]: { commonHeader, payload } } }
   * Callers that need the RTTLive-friendly shape should use
   * telemetryService.getLiveTelemetry(), which transforms this.
   */
  async getLiveTelemetry(systemId, sessionId) {
    if (!systemId || !sessionId) return null;
    return this._fetch(
      `/sessions/${encodeURIComponent(systemId)}/${encodeURIComponent(sessionId)}/telemetry/latest`
    );
  }

  /**
   * Start or schedule a new session.
   * config: { systemId, collectorId, durationMins, scheduled, scheduledTime (ISO string), schedulingMode, actions[] }
   */
  async startSession(config = {}) {
    const { systemId, collectorId, durationMins = null, scheduled = false, scheduledTime = null, schedulingMode = 'FIXED', actions = [] } = config;
    const actionPayloads = actions.map(buildActionPayload).filter(Boolean);

    if (scheduled) {
      const body = {
        systemId: systemId || '',
        collectorId: DEFAULT_COLLECTOR_ID,
        scheduledBy: this._resolveUser(),
        scheduledTime: scheduledTime || new Date().toISOString(),
        sessionTimeInMins: durationMins || 0,
        schedulingMode,
        actions: actionPayloads,
      };
      const data = await this._fetch('/schedule-session', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return transformScheduledSession(data);
    }

    const body = {
      systemId: systemId || '',
      collectorId: DEFAULT_COLLECTOR_ID,
      user: this._resolveUser(),
      sessionTimeInMins: durationMins || 0,
      actions: actionPayloads,
    };
    const data = await this._fetch('/start-session', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return transformSession(data);
  }

  /** Cancel a scheduled session (only while status=SCHEDULED). */
  async cancelScheduledSession(scheduledSessionId) {
    await this._fetch(`/cancel-scheduled-session/${encodeURIComponent(scheduledSessionId)}`, {
      method: 'PATCH',
    });
  }

  /** Stop a running session. */
  async stopSession(sessionId, systemId = '') {
    const sid = systemId || this._systemIdCache?.[sessionId] || '';
    const body = {
      sessionId,
      systemId: sid,
      user: this._resolveUser(),
      userRole: this._userRole,
    };

    const data = await this._fetch('/end-session', {
      method: 'DELETE',
      body: JSON.stringify(body),
    });

    return transformSession(data);
  }

  /**
   * Add an action to a running session.
   * Rebuilds the complete actions array and sends a full PUT (declarative contract).
   */
  async addAction(sessionId, action, currentSession = null) {
    const newActions = [...(currentSession?.actions || []), action];
    return this._updateSessionActions(sessionId, currentSession?.systemId, newActions, currentSession);
  }

  /** Update an existing action. */
  async updateAction(sessionId, actionId, updatedFields, currentSession = null) {
    const newActions = (currentSession?.actions || []).map((a) =>
      a.id === actionId ? { ...a, ...updatedFields, id: actionId } : a
    );
    return this._updateSessionActions(sessionId, currentSession?.systemId, newActions, currentSession);
  }

  /** Remove an action from a running session. */
  async removeAction(sessionId, actionId, currentSession = null) {
    const newActions = (currentSession?.actions || []).filter((a) => a.id !== actionId);
    return this._updateSessionActions(sessionId, currentSession?.systemId, newActions, currentSession);
  }

  /**
   * Replace the full actions array for a running session with a single PUT.
   * Used by the review modal after the user approves staged local changes.
   */
  async replaceActions(sessionId, systemId, newActions, currentSession = null) {
    return this._updateSessionActions(sessionId, systemId, newActions, currentSession);
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /**
   * Send PUT /update-session with the complete declarative actions array.
   * Returns an updated local session object (mirrors addAction/updateAction/removeAction
   * behaviour so components calling onSessionChanged() still work).
   */
  async _updateSessionActions(sessionId, systemId, newActions, currentSession) {
    const sid = systemId || this._systemIdCache?.[sessionId] || '';
    const body = {
      sessionId,
      systemId: sid,
      collectorId: DEFAULT_COLLECTOR_ID,
      user: this._resolveUser(),
      userRole: this._userRole,
      actions: newActions.map(buildActionPayload).filter(Boolean),
    };

    await this._fetch('/update-session', {
      method: 'PUT',
      body: JSON.stringify(body),
    });

    // Backend returns the updated session; if not, reconstruct locally so components
    // see the correct updated state without a refetch.
    const updated = {
      ...(currentSession || {}),
      sessionId,
      actions: newActions,
      actionCount: newActions.length,
      version: bumpVersion(currentSession?.version || 'v1'),
      configSummary: summarizeActions(newActions),
      actionTypeCounts: countActionTypes(newActions),
    };
    return updated;
  }
}

// ── Singleton ────────────────────────────────────────────────────────────────

const realTimeDeviceService = new RealTimeDeviceService();
export default realTimeDeviceService;
